// RoundRobinViewModePage — Admin-only TV/presentation surface for round robins.
//
// Route: /round-robins/:id/view (wrapped in <AdminRoute> in App.js).
// Renders NO app chrome.
//
// View options:
//   • schedule  — MatchScheduleGrid over rr.matches (excludes final placeholder
//                 with no entries — same behavior as public schedule view).
//   • standings — PresentationStandings using rr.standings (pre-computed
//                 by the RR engine).
//
// Data source: GET /api/round-robins/{id} — no new backend routes.
// Refresh: silent poll every 15s.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { roundRobinAPI } from '../lib/roundRobinApi';
import ViewModeShell from '../components/viewmode/ViewModeShell';
import MatchScheduleSlates from '../components/viewmode/MatchScheduleSlates';
import PresentationStandings from '../components/viewmode/PresentationStandings';

const REFRESH_MS = 15000;

const VIEW_OPTIONS = [
  { key: 'schedule',  label: 'Schedule'  },
  { key: 'standings', label: 'Standings' },
];

export default function RoundRobinViewModePage() {
  const { id } = useParams();
  const [rr, setRr] = useState(null);
  const [err, setErr] = useState(null);
  const [activeView, setActiveView] = useState('schedule');
  const [lastUpdated, setLastUpdated] = useState(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await roundRobinAPI.get(id);
      if (!mounted.current) return;
      setRr(r.data);
      setLastUpdated(new Date());
      setErr(null);
    } catch (e) {
      if (!mounted.current) return;
      setErr(e.response?.data?.detail || 'Could not load round robin.');
    }
  }, [id]);

  useEffect(() => {
    mounted.current = true;
    load();
    const int = setInterval(load, REFRESH_MS);
    return () => {
      mounted.current = false;
      clearInterval(int);
    };
  }, [load]);

  if (err && !rr) {
    return <Navigate to={`/round-robins/${id}`} replace />;
  }

  const viewLabel = activeView === 'schedule' ? 'Match Schedule' : 'Overall Standings';

  return (
    <ViewModeShell
      eventName={rr?.name}
      viewLabel={viewLabel}
      viewOptions={VIEW_OPTIONS}
      activeView={activeView}
      onViewChange={setActiveView}
      lastUpdated={lastUpdated}
    >
      {!rr ? (
        <div
          className="h-full flex items-center justify-center text-[#6a766e]"
          style={{ fontSize: 'clamp(14px, 1.2vw, 18px)' }}
          data-testid="rr-view-loading"
        >
          Loading…
        </div>
      ) : (
        <>
          <div className={activeView === 'schedule' ? 'h-full' : 'hidden'}>
            <MatchScheduleSlates
              matches={rr.matches || []}
              mode="pool"
              poolLabels={rr.pool_labels || {}}
              championshipFormat={rr.championship_format}
              poolChampionships={rr.pool_championships || []}
              active={activeView === 'schedule'}
              testidPrefix="rr-view-schedule"
            />
          </div>
          <div className={activeView === 'standings' ? 'h-full' : 'hidden'}>
            <PresentationStandings rr={rr} />
          </div>
        </>
      )}
    </ViewModeShell>
  );
}
